import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  VStack,
  Text,
  Icon,
} from "@chakra-ui/react";
import { FiSearch, FiCalendar, FiPhone, FiHash, FiRefreshCcw } from "react-icons/fi";

interface BbpsComplaintSearchProps {
  onSearch: (filters: {
    mobileNo?: string;
    fromDate?: string;
    toDate?: string;
    orderId?: string;
    txnReferenceId?: string;
  }) => void;
  onReset: () => void;
  loading?: boolean;
}

const BbpsComplaintSearch: React.FC<BbpsComplaintSearchProps> = ({ onSearch, onReset, loading }) => {
  const [mobileNo, setMobileNo] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchId, setSearchId] = useState("");

  const handleSearchByMobileAndDate = () => {
    onSearch({
      mobileNo: mobileNo || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    });
  };

  const handleSearchById = () => {
    const isOrderId = /^\d+$/.test(searchId);
    onSearch({
      orderId: isOrderId ? searchId : undefined,
      txnReferenceId: !isOrderId ? searchId : undefined,
    });
  };

  const clearSearch = () => {
    setMobileNo("");
    setFromDate("");
    setToDate("");
    setSearchId("");
    onReset();
  };

  return (
    <Box bg="white" p={4} rounded="lg" shadow="sm" mb={4} borderWidth="1px" borderColor="gray.100">
      <VStack align="stretch" gap={4}>
        <Flex justify="space-between" align="flex-start">
          <Text fontWeight="bold" color="gray.700" fontSize="sm">
            Search Transactions / Complaints
          </Text>
          <VStack align="flex-end" gap={1}>
            <Button 
                variant="ghost" 
                size="xs" 
                color="blue.500"
                onClick={clearSearch}
                height="auto"
                padding={0}
            >
                <FiRefreshCcw style={{ marginRight: "4px" }} /> Reset
            </Button>
          </VStack>
        </Flex>
        
        {/* Option 1: Mobile & Date */}
        <Flex direction={{ base: "column", md: "row" }} gap={4} align="flex-end">
          <Box flex={1.5} width="100%">
            <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1}>Mobile Number</Text>
            <Flex align="center" position="relative">
                <Box position="absolute" left={3} zIndex={1}>
                    <Icon as={FiPhone} color="gray.300" />
                </Box>
                <Input 
                    placeholder="Mobile No" 
                    value={mobileNo} 
                    onChange={(e) => setMobileNo(e.target.value)} 
                    rounded="md"
                    size="sm"
                    paddingLeft="36px"
                />
            </Flex>
          </Box>
          
          <Box flex={1} width="100%">
            <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1}>From Date</Text>
            <Flex align="center" position="relative">
                <Box position="absolute" left={3} zIndex={1}>
                    <Icon as={FiCalendar} color="gray.300" />
                </Box>
                <Input 
                    type="date" 
                    value={fromDate} 
                    onChange={(e) => setFromDate(e.target.value)} 
                    rounded="md"
                    size="sm"
                    paddingLeft="36px"
                />
            </Flex>
          </Box>
          
          <Box flex={1} width="100%">
            <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1}>To Date</Text>
            <Flex align="center" position="relative">
                <Box position="absolute" left={3} zIndex={1}>
                    <Icon as={FiCalendar} color="gray.300" />
                </Box>
                <Input 
                    type="date" 
                    value={toDate} 
                    onChange={(e) => setToDate(e.target.value)} 
                    rounded="md"
                    size="sm"
                    paddingLeft="36px"
                />
            </Flex>
          </Box>
          
          <Button 
            color="white"
            bg="blue.500" 
            _hover={{ bg: "blue.600" }}
            size="sm" 
            px={6}
            onClick={handleSearchByMobileAndDate}
            disabled={(!mobileNo && !fromDate && !toDate) || loading}
            loading={loading}
            rounded="md"
          >
            <FiSearch style={{ marginRight: "6px" }} /> Search
          </Button>
        </Flex>

        <Flex align="center" gap={4} py={1}>
            <Box flex={1} h="1px" bg="gray.100" />
            <Text fontSize="10px" color="gray.300" fontWeight="black" letterSpacing="widest">OR</Text>
            <Box flex={1} h="1px" bg="gray.100" />
        </Flex>

        {/* Option 2: ID Search */}
        <Flex direction={{ base: "column", md: "row" }} gap={4} align="flex-end">
          <Box flex={3} width="100%">
            <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1}>Order ID / Ref ID</Text>
            <Flex align="center" position="relative">
                <Box position="absolute" left={3} zIndex={1}>
                    <Icon as={FiHash} color="gray.300" />
                </Box>
                <Input 
                    placeholder="Enter Order ID or Transaction Reference ID" 
                    value={searchId} 
                    onChange={(e) => setSearchId(e.target.value)} 
                    rounded="md"
                    size="sm"
                    paddingLeft="36px"
                />
            </Flex>
          </Box>
          
          <Button 
            color="white"
            bg="teal.500" 
            _hover={{ bg: "teal.600" }}
            size="sm" 
            px={10}
            onClick={handleSearchById}
            disabled={!searchId || loading}
            loading={loading}
            rounded="md"
          >
            <FiSearch style={{ marginRight: "6px" }} /> Search by ID
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};

export default BbpsComplaintSearch;
